"use client";

import Link from "next/link";
import type { ReactNode } from "react";

export type DividerRowItem = {
  key: string;
  /** When set, the cell renders as a hoverable <Link> instead of a plain div. */
  href?: string;
  content: ReactNode;
};

type DividerRowProps = {
  items: DividerRowItem[];
  className?: string;
  /** Per-cell layout classes. Defaults to the centered stat-tile look (Stats section / Trust Signals row). */
  cellClassName?: string;
  background?: string;
  borderColor?: string;
};

const DEFAULT_CELL = "flex flex-1 items-center justify-center min-w-0 px-4 py-4 sm:px-4 sm:py-5";

/**
 * Shared "glass row split into equal columns by vertical hairlines" shell -
 * the structural pattern behind the Hero's embedded stats, the Stats
 * section, and the Trust Signals row. Each item renders as a plain flex
 * cell, or as a hoverable <Link> when it has an `href`. Presentational
 * specifics (alignment, gap, padding) stay per-section via `cellClassName`
 * so each instance keeps its original spacing.
 */
export default function DividerRow({
  items,
  className = "",
  cellClassName = DEFAULT_CELL,
  background = "var(--glass-bg, rgba(16,24,39,0.55))",
  borderColor = "var(--color-card-border, rgba(91,127,255,0.18))",
}: DividerRowProps) {
  return (
    <div
      className={`section-panel flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-white/10 rounded-[26px] border overflow-hidden ${className}`}
      style={{
        background,
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        borderColor,
        boxShadow: "var(--brand-panel-shadow, 0 24px 70px -36px rgba(0,0,0,0.9))",
      }}
    >
      {items.map((item) =>
        item.href ? (
          <Link key={item.key} href={item.href} className={`group transition-colors hover:bg-white/[0.03] ${cellClassName}`}>
            {item.content}
          </Link>
        ) : (
          <div key={item.key} className={cellClassName}>
            {item.content}
          </div>
        )
      )}
    </div>
  );
}
